import { useCallback, useMemo, useState } from 'react'
import { useParams, Link } from '@tanstack/react-router'
import { useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { Button } from './ui/button'
import { Input } from './ui/input'
import { Badge } from './ui/badge'
import { Label } from './ui/label'
import { Textarea } from './ui/textarea'
import { Checkbox } from './ui/checkbox'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './ui/dialog'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select'
import { mdiLoading, mdiPlus } from '@mdi/js'
import { AppIcon } from './ui/icon'
import { useBreadcrumbs } from '../hooks/useBreadcrumbs'
import { $api } from '../api/queryClient'
import { useAssetTypes } from '../api/queries'
import { ApiSearchableSelect, type ApiSearchableSelectOption } from './ApiSearchableSelect'
import { usePropertyDefinitionSearch } from '../hooks/usePropertyDefinitionSearch'
import { AggregationConditionEditor, isConditionComplete, type AggregationCondition } from './aggregations/AggregationConditionEditor'
import type { AggregationDetail, CreateAggregationRequest } from '../api/schema'
import { Can } from '../contexts/PermissionsContext'
import { PageHeader } from './shared/PageHeader'
import { SECTION_ICONS } from '../lib/section-icons'
import { AssetTypeTile, type AssetTypeLike } from './assets/AssetTypeTile'
import { EmptyState } from './ui/empty-state'

type AggregationFunctionCode = NonNullable<CreateAggregationRequest['function']>

/**
 * A sum/avg/min/max aggregation reads either a property on the children or one
 * of the children's own `assets.*` columns — never both; the API rejects the
 * pair with a 422 (`aggregations/service.go`, `validateSourceAssetColumn`).
 *
 * The column list mirrors the backend allowlist `AllowedSourceAssetColumns`.
 * Spec 2026-09-03-02 introduced it: `assets.start_date` became an equipment's
 * commissioning date, so the building fleet-age aggregations read a column
 * rather than the retired `commissioning_year` property. A date column
 * aggregates as its year, which is why its target property stays `number`.
 */
const SOURCE_ASSET_COLUMNS = ['start_date', 'end_date'] as const
type SourceKind = 'property' | 'asset_column'

const FUNCTIONS: Array<{ value: AggregationFunctionCode; needsSource: boolean; needsFilter: boolean }> = [
  { value: 'count', needsSource: false, needsFilter: false },
  { value: 'count_where', needsSource: false, needsFilter: true },
  { value: 'count_where_observed', needsSource: false, needsFilter: true },
  { value: 'sum', needsSource: true, needsFilter: false },
  { value: 'avg', needsSource: true, needsFilter: false },
  { value: 'min', needsSource: true, needsFilter: false },
  { value: 'max', needsSource: true, needsFilter: false },
  { value: 'ratio', needsSource: false, needsFilter: true },
]

function invalidateAggregations(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({
    predicate: (query) => {
      const queryKey = query.queryKey as any[]
      if (queryKey.length >= 2 && queryKey[0] === 'get') {
        return (queryKey[1] as string).includes('/aggregations')
      }
      return false
    }
  })
}

export function AggregationsPage() {
  const { org } = useParams({ strict: false })
  const queryClient = useQueryClient()
  const { t } = useTranslation('aggregations')
  const [searchQuery, setSearchQuery] = useState('')
  const [enabledFilter, setEnabledFilter] = useState<string>('all')
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)

  const [newAggregation, setNewAggregation] = useState({
    code: '',
    name: { en: '', fr: '' },
    description: '',
    function: '' as AggregationFunctionCode | '',
    target_asset_type_code: '',
    target_property_code: '',
    child_asset_type_code: '',
    source_property_code: '',
    source_asset_column: '',
    enabled: true,
  })
  const [sourceKind, setSourceKind] = useState<SourceKind>('property')
  const [filterCondition, setFilterCondition] = useState<AggregationCondition | null>(null)
  const [ratioDenominatorCondition, setRatioDenominatorCondition] = useState<AggregationCondition | null>(null)

  const { data: assetTypes = [] } = useAssetTypes(org!)
  const { searchPropertyDefinitions, initialOptions } = usePropertyDefinitionSearch(org!)

  const selectedFn = FUNCTIONS.find(f => f.value === newAggregation.function)

  useBreadcrumbs(useMemo(() => [
    { title: t('common:nav.home'), path: `/${org}` },
    { title: t('common:nav.settings'), path: `/${org}/settings` },
    { title: t('breadcrumb') }
  ], [org, t]))

  const queryParams: Record<string, any> = {}
  if (searchQuery) queryParams.q = searchQuery
  if (enabledFilter !== 'all') queryParams.enabled = enabledFilter === 'true'

  const { data, isLoading, error } = $api.useQuery('get', '/api/v1/organizations/{orgCode}/aggregations', {
    params: {
      path: { orgCode: org! },
      query: Object.keys(queryParams).length > 0 ? queryParams : undefined,
    }
  })

  const createMutation = $api.useMutation('post', '/api/v1/organizations/{orgCode}/aggregations', {
    onSuccess: () => {
      invalidateAggregations(queryClient)
      setIsCreateDialogOpen(false)
      setNewAggregation({
        code: '', name: { en: '', fr: '' }, description: '',
        function: '', target_asset_type_code: '', target_property_code: '',
        child_asset_type_code: '', source_property_code: '', source_asset_column: '',
        enabled: true,
      })
      setSourceKind('property')
      setFilterCondition(null)
      setRatioDenominatorCondition(null)
    }
  })

  const aggregations: AggregationDetail[] = data?.data || []
  const total = data?.total || 0

  const handlePropertySelect = useCallback((option: ApiSearchableSelectOption) => {
    const code = (option.data?.code as string) || ''
    setNewAggregation(prev => ({ ...prev, target_property_code: code }))
  }, [])

  const filterConditionValid = !selectedFn?.needsFilter || isConditionComplete(filterCondition)
  const ratioConditionValid = newAggregation.function !== 'ratio' || isConditionComplete(ratioDenominatorCondition)
  // sum/avg/min/max need exactly one source, and the picked kind decides which
  // field has to be filled — submitting neither produced a server-side failure
  // with nothing on screen to explain it.
  const sourceValid =
    !selectedFn?.needsSource ||
    (sourceKind === 'property'
      ? !!newAggregation.source_property_code
      : !!newAggregation.source_asset_column)
  const formValid = !!newAggregation.code && !!newAggregation.function && filterConditionValid && ratioConditionValid && sourceValid

  const handleCreate = () => {
    if (!formValid) return

    createMutation.mutate({
      params: { path: { orgCode: org! } },
      body: {
        code: newAggregation.code,
        name: newAggregation.name,
        description: newAggregation.description || undefined,
        function: newAggregation.function as AggregationFunctionCode,
        target_asset_type_code: newAggregation.target_asset_type_code,
        target_property_code: newAggregation.target_property_code,
        child_asset_type_code: newAggregation.child_asset_type_code,
        // Mutually exclusive by construction: whichever source the user did
        // not pick is sent as undefined rather than as an empty string, which
        // the API would read as "both given".
        source_property_code:
          selectedFn?.needsSource && sourceKind === 'property'
            ? newAggregation.source_property_code
            : undefined,
        source_asset_column:
          selectedFn?.needsSource && sourceKind === 'asset_column'
            ? newAggregation.source_asset_column
            : undefined,
        // See AggregationDetailPage.tsx for why this narrow cast is the one
        // legitimate remaining `as any`: the schema types these fields as an
        // empty `object` with no properties.
        filter_conditions: (filterCondition ?? null) as unknown as Record<string, never> | null,
        ratio_denominator_conditions: (ratioDenominatorCondition ?? null) as unknown as Record<string, never> | null,
        enabled: newAggregation.enabled,
      }
    })
  }

  return (
    <div className="container mx-auto py-8 max-w-6xl">
      <div className="bg-card rounded-lg shadow-lg">
        <div className="px-6 py-4 border-b border-border">
          <PageHeader
            icon={SECTION_ICONS.aggregations}
            title={t('page_title')}
            description={t('page_description', { count: total })}
            actions={
              <>
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <Can perm="configuration.write">
              <DialogTrigger asChild>
                <Button data-testid="create-aggregation-btn">
                  <AppIcon path={mdiPlus} size="sm" className="mr-2" />
                  {t('create_button')}
                </Button>
              </DialogTrigger>
            </Can>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{t('create_dialog.title')}</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 p-4">
                <div>
                  <Label htmlFor="code">{t('create_dialog.code_label')}</Label>
                  <Input
                    id="code"
                    placeholder={t('create_dialog.code_placeholder')}
                    className="font-mono"
                    value={newAggregation.code}
                    onChange={(e) => setNewAggregation(prev => ({ ...prev, code: e.target.value }))}
                    data-testid="create-aggregation-code"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="name-en">{t('create_dialog.name_en_label')}</Label>
                    <Input
                      id="name-en"
                      value={newAggregation.name.en}
                      onChange={(e) => setNewAggregation(prev => ({ ...prev, name: { ...prev.name, en: e.target.value } }))}
                      data-testid="create-aggregation-name-en"
                    />
                  </div>
                  <div>
                    <Label htmlFor="name-fr">{t('create_dialog.name_fr_label')}</Label>
                    <Input
                      id="name-fr"
                      value={newAggregation.name.fr}
                      onChange={(e) => setNewAggregation(prev => ({ ...prev, name: { ...prev.name, fr: e.target.value } }))}
                      data-testid="create-aggregation-name-fr"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="description">{t('create_dialog.description_label')}</Label>
                  <Textarea
                    id="description"
                    value={newAggregation.description}
                    onChange={(e) => setNewAggregation(prev => ({ ...prev, description: e.target.value }))}
                    data-testid="create-aggregation-description"
                  />
                </div>
                <div>
                  <Label>{t('create_dialog.function_label')}</Label>
                  <Select
                    value={newAggregation.function}
                    onValueChange={(v) => setNewAggregation(prev => ({ ...prev, function: v as AggregationFunctionCode }))}
                  >
                    <SelectTrigger data-testid="create-aggregation-function">
                      <SelectValue placeholder={t('create_dialog.function_placeholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {FUNCTIONS.map(fn => (
                        <SelectItem key={fn.value} value={fn.value}>
                          {t(`functions.${fn.value}`)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>{t('create_dialog.target_asset_type_label')}</Label>
                  <Select
                    value={newAggregation.target_asset_type_code}
                    onValueChange={(v) => setNewAggregation(prev => ({ ...prev, target_asset_type_code: v }))}
                  >
                    <SelectTrigger data-testid="create-aggregation-target-type">
                      <SelectValue placeholder={t('create_dialog.target_asset_type_placeholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {assetTypes.map((at: any) => (
                        <SelectItem key={at.uid} value={at.code}>
                          <span className="flex items-center gap-2">
                            <AssetTypeTile type={at as AssetTypeLike} size="sm" />
                            <span>{typeof at.name === 'string' ? at.name : at.name?.en || at.name?.fr || at.code}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">{t('create_dialog.target_asset_type_help')}</p>
                </div>
                <div>
                  <Label>{t('create_dialog.target_property_label')}</Label>
                  <ApiSearchableSelect
                    placeholder={t('create_dialog.target_property_placeholder')}
                    onSearch={searchPropertyDefinitions}
                    onOptionSelect={handlePropertySelect}
                    initialOptions={initialOptions}
                    data-testid="create-aggregation-target-prop"
                  />
                  <p className="text-xs text-muted-foreground mt-1">{t('create_dialog.target_property_help')}</p>
                </div>
                <div>
                  <Label>{t('create_dialog.child_asset_type_label')}</Label>
                  <Select
                    value={newAggregation.child_asset_type_code}
                    onValueChange={(v) => setNewAggregation(prev => ({ ...prev, child_asset_type_code: v }))}
                  >
                    <SelectTrigger data-testid="create-aggregation-child-type">
                      <SelectValue placeholder={t('create_dialog.child_asset_type_placeholder')} />
                    </SelectTrigger>
                    <SelectContent>
                      {assetTypes.map((at: any) => (
                        <SelectItem key={at.uid} value={at.code}>
                          <span className="flex items-center gap-2">
                            <AssetTypeTile type={at as AssetTypeLike} size="sm" />
                            <span>{typeof at.name === 'string' ? at.name : at.name?.en || at.name?.fr || at.code}</span>
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-muted-foreground mt-1">{t('create_dialog.child_asset_type_help')}</p>
                </div>

                {selectedFn?.needsSource && (
                  <div className="space-y-3">
                    <div>
                      <Label>{t('create_dialog.source_kind_label')}</Label>
                      <Select
                        value={sourceKind}
                        onValueChange={(v) => setSourceKind(v as SourceKind)}
                      >
                        <SelectTrigger data-testid="create-aggregation-source-kind">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="property">{t('create_dialog.source_kind_property')}</SelectItem>
                          <SelectItem value="asset_column">{t('create_dialog.source_kind_asset_column')}</SelectItem>
                        </SelectContent>
                      </Select>
                      <p className="text-xs text-muted-foreground mt-1">{t('create_dialog.source_kind_help')}</p>
                    </div>

                    {sourceKind === 'property' ? (
                      <div>
                        <Label htmlFor="source-prop">{t('create_dialog.source_property_label')}</Label>
                        <Input
                          id="source-prop"
                          className="font-mono"
                          placeholder={t('create_dialog.source_property_placeholder')}
                          value={newAggregation.source_property_code}
                          onChange={(e) => setNewAggregation(prev => ({ ...prev, source_property_code: e.target.value }))}
                          data-testid="create-aggregation-source-prop"
                        />
                        <p className="text-xs text-muted-foreground mt-1">{t('create_dialog.source_property_help')}</p>
                      </div>
                    ) : (
                      <div>
                        <Label>{t('create_dialog.source_asset_column_label')}</Label>
                        <Select
                          value={newAggregation.source_asset_column}
                          onValueChange={(v) => setNewAggregation(prev => ({ ...prev, source_asset_column: v }))}
                        >
                          <SelectTrigger data-testid="create-aggregation-source-column">
                            <SelectValue placeholder={t('create_dialog.source_asset_column_placeholder')} />
                          </SelectTrigger>
                          <SelectContent>
                            {SOURCE_ASSET_COLUMNS.map(column => (
                              <SelectItem key={column} value={column} data-testid={`create-aggregation-source-column-${column}`}>
                                <span className="font-mono">assets.{column}</span>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <p className="text-xs text-muted-foreground mt-1">{t('create_dialog.source_asset_column_help')}</p>
                      </div>
                    )}
                  </div>
                )}

                {selectedFn?.needsFilter && (
                  <AggregationConditionEditor
                    org={org!}
                    label={t('create_dialog.filter_conditions_label')}
                    helpText={t('create_dialog.filter_conditions_help')}
                    value={filterCondition}
                    onChange={setFilterCondition}
                    data-testid="create-aggregation-filter"
                  />
                )}

                {newAggregation.function === 'ratio' && (
                  <AggregationConditionEditor
                    org={org!}
                    label={t('create_dialog.ratio_denominator_label')}
                    helpText={t('create_dialog.ratio_denominator_help')}
                    value={ratioDenominatorCondition}
                    onChange={setRatioDenominatorCondition}
                    data-testid="create-aggregation-ratio-denom"
                  />
                )}

                <div className="flex items-center space-x-2">
                  <Checkbox
                    checked={newAggregation.enabled}
                    onCheckedChange={(checked) => setNewAggregation(prev => ({ ...prev, enabled: checked === true }))}
                    data-testid="create-aggregation-enabled"
                  />
                  <Label>{t('create_dialog.enabled_label')}</Label>
                </div>

                {/* Say which requirement is unmet. The condition message used to
                    be shown for every reason the form was invalid; now that a
                    missing source can also block submission, naming the wrong
                    one would send the user to edit a condition that is fine. */}
                {!formValid && (newAggregation.code || newAggregation.function) && (
                  <p className="text-xs text-destructive" data-testid="create-aggregation-condition-error">
                    {!sourceValid
                      ? t('create_dialog.source_required_error')
                      : t('create_dialog.condition_incomplete_error')}
                  </p>
                )}

                <Button
                  onClick={handleCreate}
                  disabled={createMutation.isPending || !formValid}
                  data-testid="create-aggregation-submit"
                  className="w-full"
                >
                  {createMutation.isPending ? <AppIcon path={mdiLoading} size="sm" className="mr-2 motion-safe:animate-spin" /> : null}
                  {t('create_dialog.submit')}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
              </>
            }
          />
        </div>

        <div className="p-6">
          <div className="flex gap-4 mb-4">
            <Input
              placeholder={t('search_placeholder')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="max-w-sm"
              data-testid="aggregations-search"
            />
            <Select value={enabledFilter} onValueChange={setEnabledFilter}>
              <SelectTrigger className="w-[180px]" data-testid="aggregations-enabled-filter">
                <SelectValue placeholder={t('filter_status_placeholder')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">{t('common:status.all')}</SelectItem>
                <SelectItem value="true">{t('common:status.enabled')}</SelectItem>
                <SelectItem value="false">{t('common:status.disabled')}</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {isLoading ? (
            <div className="flex justify-center py-8">
              <AppIcon path={mdiLoading} size="xl" className="motion-safe:animate-spin" />
            </div>
          ) : error ? (
            <div className="text-destructive text-center py-8">{t('failed_to_load')}</div>
          ) : aggregations.length === 0 ? (
            <EmptyState variant="inline" live title={t('no_aggregations')} />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('table.name')}</TableHead>
                  <TableHead>{t('table.code')}</TableHead>
                  <TableHead>{t('table.function')}</TableHead>
                  <TableHead>{t('table.target')}</TableHead>
                  <TableHead>{t('table.child_type')}</TableHead>
                  <TableHead>{t('table.enabled')}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {aggregations.map((agg) => {
                  const targetAssetType = assetTypes.find((at: any) => at.code === agg.target_asset_type?.code || at.uid === agg.target_asset_type?.uid) as any
                  const childAssetType = assetTypes.find((at: any) => at.code === agg.child_asset_type_code) as any
                  return (
                    <TableRow key={agg.uid}>
                      <TableCell>
                        <Link
                          to="/$org/aggregations/$uid"
                          params={{ org: org!, uid: agg.uid }}
                          className="text-primary hover:underline font-medium"
                          data-testid={`aggregation-link-${agg.code}`}
                        >
                          {agg.name?.en || agg.name?.fr || agg.code}
                        </Link>
                      </TableCell>
                      <TableCell>
                        <code className="text-sm bg-muted px-1 py-0.5 rounded">{agg.code}</code>
                      </TableCell>
                      <TableCell>
                        <Badge variant="secondary">{t(`functions.${agg.function}`, { defaultValue: agg.function })}</Badge>
                      </TableCell>
                      <TableCell>
                        <span>
                          {agg.target_property?.name?.en || agg.target_property?.code || '-'}
                          {targetAssetType && (
                            <span className="text-muted-foreground ml-1">
                              {t('common:on')} {typeof targetAssetType.name === 'string' ? targetAssetType.name : targetAssetType.name?.en || targetAssetType.code}
                            </span>
                          )}
                        </span>
                      </TableCell>
                      <TableCell>
                        {childAssetType ? (
                          <span>
                            {typeof childAssetType.name === 'string' ? childAssetType.name : childAssetType.name?.en || childAssetType.code}
                          </span>
                        ) : agg.child_asset_type_code || '-'}
                      </TableCell>
                      <TableCell>
                        <Badge variant={agg.enabled ? 'default' : 'secondary'}>
                          {agg.enabled ? t('common:status.enabled') : t('common:status.disabled')}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          )}
        </div>
      </div>
    </div>
  )
}
